import { Box, RingProgress, Text, useMantineTheme } from '@mantine/core';
import { forwardRef, ReactNode } from 'react';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface RingProps {
  value: number;
  label?: ReactNode;
  size?: number;
  thickness?: number;
  gradientVariant?: GradientVariant;
  showValue?: boolean;
  animated?: boolean;
}

const Ring = forwardRef<HTMLDivElement, RingProps>(
  (
    {
      value,
      label,
      size = 120,
      thickness = 12,
      gradientVariant = 'primary',
      showValue = true,
      animated = false,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other.gradients[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Create gradient sections
    const sections = [
      { value, color: gradient.from },
    ];

    return (
      <Box ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
        {/* Background ring with gradient overlay */}
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: size,
            height: size,
            borderRadius: '50%',
            background: `conic-gradient(from 0deg, ${gradient.from} 0%, ${gradient.to} ${value}%, transparent ${value}%)`,
            mask: `radial-gradient(transparent ${size / 2 - thickness}px, black ${size / 2 - thickness}px)`,
            WebkitMask: `radial-gradient(transparent ${size / 2 - thickness}px, black ${size / 2 - thickness}px)`,
            animation: animated ? 'ring-rotate 2s linear infinite' : undefined,
          }}
        />

        <RingProgress
          size={size}
          thickness={thickness}
          sections={sections}
          label={
            showValue ? (
              <Text ta="center" fw={700} size="lg" style={accessibilityStyles}>
                {label || `${value}%`}
              </Text>
            ) : (
              label
            )
          }
          styles={{
            root: { position: 'relative' },
          }}
        />

        {animated && (
          <style>{`
            @keyframes ring-rotate {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        )}
      </Box>
    );
  }
);

Ring.displayName = 'Ring';

export default Ring;
